import React, { useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import {
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import {
  fetchConversationListing,
  fetchConversationThread,
  setSelectedConversationId,
} from 'src/redux/chatbotConversations'
import { formatDate, truncateText, useDebouncedFilter } from './helpers'

const ConversationTable = () => {
  const theme = useTheme()
  const dispatch = useDispatch()
  const { conversations, conversationsLoading, conversationsTotalCount, selectedConversationId } =
    useSelector(state => state.chatbotConversations)

  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)

  const { filterValue: emailFilter, handleFilterChange: handleEmailFilterChange } =
    useDebouncedFilter(value => {
      setPage(0)
      dispatch(fetchConversationListing({ userEmail: value || undefined, page: 1, pageSize: rowsPerPage }))
    })

  const handleRowClick = conversationId => {
    dispatch(setSelectedConversationId(conversationId))
    dispatch(fetchConversationThread(conversationId))
  }

  const handlePageChange = (_event, newPage) => {
    setPage(newPage)
    dispatch(
      fetchConversationListing({
        userEmail: emailFilter || undefined,
        page: newPage + 1,
        pageSize: rowsPerPage,
      }),
    )
  }

  const handleRowsPerPageChange = event => {
    const newRowsPerPage = parseInt(event.target.value, 10)
    setRowsPerPage(newRowsPerPage)
    setPage(0)
    dispatch(
      fetchConversationListing({
        userEmail: emailFilter || undefined,
        page: 1,
        pageSize: newRowsPerPage,
      }),
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.grey[200]}`, flexShrink: 0 }}>
        <TextField
          label='Filter by email'
          value={emailFilter}
          onChange={handleEmailFilterChange}
          size='small'
          fullWidth
        />
      </Box>

      {conversationsLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress size={32} />
        </Box>
      ) : (
        <Box sx={{ flex: 1, overflowY: 'auto' }}>
          <Table size='small' stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>User Email</TableCell>
                <TableCell>First Message</TableCell>
                <TableCell align='right'>Messages</TableCell>
                <TableCell align='right'>Feedback</TableCell>
                <TableCell>Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {conversations.map(conversation => (
                <TableRow
                  key={conversation.conversationId}
                  hover
                  onClick={() => handleRowClick(conversation.conversationId)}
                  selected={conversation.conversationId === selectedConversationId}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>{conversation.userEmail}</TableCell>
                  <TableCell>
                    <Typography variant='body2' color='textSecondary'>
                      {truncateText(conversation.firstMessagePreview, 60)}
                    </Typography>
                  </TableCell>
                  <TableCell align='right'>{conversation.messageCount}</TableCell>
                  <TableCell align='right'>{conversation.feedbackCount}</TableCell>
                  <TableCell>
                    <Typography variant='caption' noWrap>
                      {formatDate(conversation.lastMessageAt)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
              {conversations.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align='center'>
                    <Typography variant='body2' color='textSecondary'>
                      No conversations found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      )}

      <TablePagination
        component='div'
        count={conversationsTotalCount}
        page={page}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[10, 25, 50, 100]}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        sx={{ flexShrink: 0, borderTop: '1px solid', borderColor: 'grey.200' }}
      />
    </Box>
  )
}

export default ConversationTable
